import { create, type StateCreator } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";
import { applyResetMiddleware } from "../middleware/resetMiddleware";
import { createPrimarySelectionSlice, type PrimarySelectionSlice } from "./primarySlice";
import { createSecondarySelectionSlice, type SecondarySelectionSlice } from "./secondarySelection";
import { createFilterSelectionSlice, type FilterSlice } from "./filterSlice";

// Combined store type — union of all slices
type SelectionStore = PrimarySelectionSlice & SecondarySelectionSlice & FilterSlice;

// Slice middle-ware mutators shorthand for combined store
type SliceMutators = [["zustand/devtools", never], ["zustand/immer", never]];

export const useSelectionStore = create<SelectionStore>()(
  devtools(
    immer(
      applyResetMiddleware(((set, get, store) => ({
        ...(createPrimarySelectionSlice as StateCreator<SelectionStore, SliceMutators, [], PrimarySelectionSlice>)(set, get, store),
        ...(createSecondarySelectionSlice as StateCreator<SelectionStore, SliceMutators, [], SecondarySelectionSlice>)(set, get, store),
        ...(createFilterSelectionSlice as StateCreator<SelectionStore, SliceMutators, [], FilterSlice>)(set, get, store)
      })) as StateCreator<SelectionStore, SliceMutators, []>)
    ),
    { name: "SelectionStore" }
  )
);

// --- Unified Selectors ---

/**
 * Main Selection Selector
 * Flattens both Primary and Secondary state into a single object.
 * Uses `useShallow` to prevent unnecessary re-renders.
 */
export const useSelectionSelector = () =>
  useSelectionStore(
    useShallow((state) => ({
      // Primary State
      selectedEntityId: state.primary.selectedEntityId,
      selectedEndpoint: state.primary.selectedEndpoint,
      showRelationShip: state.primary.showRelationShip,

      // Secondary State
      secondaryItems: state.secondary.items,
      secondarySource: state.secondary.source,
      secondaryIdsByLayer: state.secondary.idsByLayer,
      secondaryItemsBbox: state.secondary.itemsBbox,

      // Filter state
      filterArea: state.filter.filterArea,
      currentFilter: state.filter.currentFilter,
      filterLayersVisible: state.filter.filterLayersVisible,
      selectedPrimaryFilterEntity: state.filter.selectedPrimaryFilterEntity,

      // Loading State
      loadingProgress: state.secondary.loadingProgress
    }))
  );

/**
 * Unified Actions Hook
 * Combines actions from both slices into a single object.
 */
export const useSelectionActions = () =>
  useSelectionStore(
    useShallow((state) => ({
      ...state.primary.actions,
      ...state.secondary.actions,
      ...state.filter.actions
    }))
  );

// Re-export slice types for external consumers
export type { PrimarySelectionSlice, SecondarySelectionSlice, FilterSlice };
export type { SecondaryItem, SecondarySource, LoadingProgress } from "./secondarySelection";
export type { PrimarySelectionState, PrimarySelectionActions } from "./primarySlice";
export type { FilterState, FilterActions } from "./filterSlice";
